import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { PlayIcon } from '../../atoms/PlayIcon';
import { NonPlayableIcon } from 'components/views/atoms/NonPlayableIcon';
import { LoadingIcon } from 'components/views/atoms/LoadingIcon';

const Frame = styled.div<{ stretch: boolean }>(props => ({
    border: '1px solid rgb(39, 155, 228)',
    backgroundColor: 'lightslategray',
    position: 'relative',
    width: props.stretch ? '100%' : undefined,
    height: props.stretch ? '100%' : undefined,
    display: 'flex',
    boxSizing: 'border-box',
}));

type VideoProps = {
    mirror: boolean;
    upsidedown: boolean;
    stretch: boolean;
    keepAspectRatio: boolean;
}

const Video = styled.video<VideoProps>(props => ({
    width: '100%',
    height: props.stretch ? '100%' : 'auto',
    transform: (props.mirror || props.upsidedown) ? [
        ...(props.mirror ? ['rotateY(180deg)'] : []),
        ...(props.upsidedown ? ['rotate(180deg)'] : []),
    ].join(' ') : 'none',
    objectFit: props.keepAspectRatio ? 'contain' : 'fill',
    position: props.stretch ? 'absolute' : undefined,
}));

type PlayProps = {
    visible: boolean;
};

const Play = styled(PlayIcon)<PlayProps>(props => ({
    visibility: props.visible ? 'visible' : 'hidden',
}));

type NonPlayableProps = {
    visible: boolean;
};

const NonPlayable = styled(NonPlayableIcon)<NonPlayableProps>(props => ({
    visibility: props.visible ? 'visible' : 'hidden',
}));

type LoadingProps = {
    visible: boolean;
};

const Loading = styled(LoadingIcon)<LoadingProps>(props => ({
    visibility: props.visible ? 'visible' : 'hidden',
}));

const IconHolder = styled.div({
    position: 'absolute',
    top: '50%',
    left: '50%',
});

function useVideoRef(stream: MediaStream | null, pause: boolean) {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (video.srcObject !== stream) {
            video.srcObject = stream;
        }

        if (pause) {
            video.pause();
        } else {
            (async () => {
                try {
                    if (!stream) return;
                    await video.play();
                } catch (err) {
                    if (err instanceof Error) {
                        console.warn('cannot start playing: ', err.message);
                    } else {
                        console.error(err);
                    }
                }
            })();
        }
    }, [stream, pause]);

    return videoRef;
}

export type VideoPlayerProps = {
    className?: string;
    stream?: MediaStream;
    pause: boolean;
    loading: boolean;
    stretch?: boolean;
    preserveAspectRatio?: boolean;
    mirror?: boolean;
    upsidedown?: boolean;
    onClick?: () => void;
    onPlaying?: () => void;
    onPause?: () => void;
    onWaiting?: () => void;
    onPlayClicked?: () => void;
}

export const VideoPlayer = (props: VideoPlayerProps) => {
    const videoRef = useVideoRef(props.stream || null, props.pause);

    return (
        <Frame className={props.className} onClick={props.onClick} stretch={props.stretch || false}>
            <Video ref={videoRef} muted={true} stretch={props.stretch || false} keepAspectRatio={props.preserveAspectRatio ?? false}
                mirror={props.mirror ?? false} upsidedown={props.upsidedown ?? false}
                onPlaying={props.onPlaying} onPause={props.onPause} onWaiting={props.onWaiting} />
            <IconHolder>
                {
                    props.onClick
                        ? <Play visible={props.pause} onClick={props.onPlayClicked} />
                        : <NonPlayable visible={props.pause} />
                }
            </IconHolder>
            <IconHolder>
                <Loading visible={props.loading} />
            </IconHolder>
        </Frame>
    );
};
